/**
 * mail-admin-handoff-v2-changes/CHANGES.md §1 -> "Recent store". Tracks the tenants *this* viewer
 * has opened, most-recently-visited first, capped at 5 — the rail's Recent section reads it
 * directly. There's no user id on GET /api/admin/me, so the storage key is the signed-in email,
 * lower-cased so "Admin@x.com" and "admin@x.com" share one list.
 *
 * The list in storage is just slugs (a Tenant can change name/domain without this module caring).
 * `recentTenants` resolves those slugs against the live `session.tenants` on every read, so a
 * renamed tenant shows its current name and a deleted one silently drops out even if `forget()`
 * was never called for it (belt-and-braces alongside TenantLayout's explicit forget-on-delete).
 *
 * Every localStorage read/write is wrapped in try/catch — private browsing, a full quota, or
 * storage disabled by policy all just mean recent tenants doesn't persist, never a thrown error.
 */
import { computed, ref } from 'vue';
import { me, tenants } from '@/session';
import type { Tenant } from '@/types';

const MAX_RECENT = 5;
const STORAGE_PREFIX = 'postnoorAdmin.recentTenants.';

export function storageKeyFor(email: string): string {
  return `${STORAGE_PREFIX}${email.trim().toLowerCase()}`;
}

/** Pure: turns a raw localStorage value into a slug list — anything not a JSON array of strings
 * (missing key, corrupt JSON, a stale non-array shape) parses to an empty list rather than throwing. */
export function parseRecentSlugs(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((slug): slug is string => typeof slug === 'string');
  } catch {
    return [];
  }
}

/** Pure: moves `slug` to the front, de-duplicating and capping at 5. */
export function addRecentSlug(slugs: string[], slug: string): string[] {
  return [slug, ...slugs.filter(existing => existing !== slug)].slice(0, MAX_RECENT);
}

/** Pure: drops `slug`, if present. */
export function removeRecentSlug(slugs: string[], slug: string): string[] {
  return slugs.filter(existing => existing !== slug);
}

function readSlugsFor(email: string): string[] {
  try {
    return parseRecentSlugs(localStorage.getItem(storageKeyFor(email)));
  } catch {
    return [];
  }
}

function writeSlugsFor(email: string, slugs: string[]): void {
  try {
    localStorage.setItem(storageKeyFor(email), JSON.stringify(slugs));
  } catch {
    // Storage unavailable — the in-memory `version` bump below still updates the rail for this
    // page view, it just won't survive a reload.
  }
}

// Bumped by every write so `recentTenants` (a computed) knows to re-read storage — reads always go
// straight to localStorage (keyed off the *current* me.value), so there's no separate ref to keep
// in sync with a signed-in user that can change (sign-out/sign-in in another tab, tests, ...).
const version = ref(0);

/** Records a visit, moving `slug` to the front of this viewer's recent list. No-op while signed out. */
export function recordVisit(slug: string): void {
  if (!me.value) return;
  writeSlugsFor(me.value.email, addRecentSlug(readSlugsFor(me.value.email), slug));
  version.value += 1;
}

/** Clears a tenant's entry, e.g. right after it's deleted. No-op while signed out. */
export function forget(slug: string): void {
  if (!me.value) return;
  writeSlugsFor(me.value.email, removeRecentSlug(readSlugsFor(me.value.email), slug));
  version.value += 1;
}

/** Up to 5 tenants this viewer opened most recently, newest first — resolved against the live
 * tenant list so a deleted tenant (forgotten or not) never appears and a renamed one shows its
 * current name. */
export const recentTenants = computed<Tenant[]>(() => {
  void version.value; // reactive dependency — see the comment above

  if (!me.value) return [];

  const bySlug = new Map(tenants.value.map(tenant => [tenant.slug, tenant] as const));
  return readSlugsFor(me.value.email)
    .map(slug => bySlug.get(slug))
    .filter((tenant): tenant is Tenant => tenant !== undefined);
});
